import { useCallback, useState } from 'react';
import { useDataProvider, useGetList, useGetOne } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { RuleTypes } from '../constants/ruleTypes';

type RuleVersionState = {
  state?: {
    record?: {
      version?: string;
    };
  };
}

export type RuleVersionRecord = {
  id: string;
  number: number;
  isTestVersion: boolean;
  isActive: boolean;
};

export type RuleVersionStatus = 'live' | 'previouslyLive' | 'testActive' | 'testDeactivated' | 'draft';

export const getRuleVersionStatus = (
  version: string,
  record: RuleVersionRecord | undefined,
  latestLiveVersionId: string | undefined,
): RuleVersionStatus | undefined => {
  if (version === 'draft') return 'draft';
  if (!record || record.id !== version) return undefined;
  if (record.isTestVersion) return record.isActive ? 'testActive' : 'testDeactivated';
  return record.id === latestLiveVersionId ? 'live' : 'previouslyLive';
};

const latestVersionParams = (ruleType: RuleTypes | undefined, isTestVersion: boolean) => ({
  filter: { ruleType, isTestVersion },
  // Same as the engine: the latest version of a kind is the one with the highest number
  sort: { field: 'number', order: 'DESC' as const },
  pagination: { page: 1, perPage: 1 },
});

const useRuleVersion = (ruleType?: RuleTypes) => {
  const {
    state,
  } = useLocation() as RuleVersionState;
  const dataProvider = useDataProvider();

  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(
    state?.record?.version,
  );

  const {
    data: latestLiveVersions,
    isPending: isLatestLivePending,
    refetch: refetchLatestLive,
  } = useGetList<RuleVersionRecord>(
    'rule-version',
    latestVersionParams(ruleType, false),
    { enabled: !!ruleType },
  );
  const latestLiveVersionId = latestLiveVersions?.[0]?.id;

  const version = selectedVersion ?? latestLiveVersionId ?? 'draft';
  const isLoading = !!ruleType && !selectedVersion && isLatestLivePending;
  const isDraft = version === 'draft';

  const { data: record, refetch: refetchRecord } = useGetOne<RuleVersionRecord>(
    'rule-version',
    { id: version },
    { enabled: !!ruleType && !isLoading && !isDraft },
  );

  const status = isLoading
    ? undefined
    : getRuleVersionStatus(version, record, latestLiveVersionId);

  const changeVersion = useCallback((v: string) => {
    if (v) setSelectedVersion(v);
  }, []);

  const resetToLatestVersion = useCallback(() => setSelectedVersion(undefined), []);

  // Publish and release do not return the id of the version they create, so it is looked up
  // as the latest version of the kind that was just created
  const selectLatestVersion = useCallback(async (isTestVersion: boolean) => {
    try {
      const [{ data }] = await Promise.all([
        dataProvider.getList<RuleVersionRecord>(
          'rule-version',
          latestVersionParams(ruleType, isTestVersion),
        ),
        refetchLatestLive(),
      ]);
      if (data[0]) setSelectedVersion(data[0].id);
    } catch {
      // The action itself succeeded, so the Member stays on the current version
    }
  }, [dataProvider, refetchLatestLive, ruleType]);

  const refetchVersion = useCallback(() => {
    refetchLatestLive();
    if (!isDraft) refetchRecord();
  }, [isDraft, refetchLatestLive, refetchRecord]);

  return {
    version,
    record: isDraft ? undefined : record,
    status,
    latestLiveVersionId,
    isDraft,
    isLoading,
    changeVersion,
    resetToLatestVersion,
    selectLatestVersion,
    refetchVersion,
  };
};

export default useRuleVersion;
